import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listPrompts,
  getPrompt,
  updatePrompt,
  activatePrompt,
} from '@/services/promptsApi'
import type { ListPromptsParams, UpdatePromptData } from '@/types'

// Hook for fetching prompt list with pagination
export function usePrompts(params: ListPromptsParams = {}) {
  return useQuery({
    queryKey: ['prompts', params],
    queryFn: () => listPrompts(params),
    staleTime: 30000, // 30 seconds
  })
}

// Hook for fetching single prompt
export function usePrompt(id: string | null) {
  return useQuery({
    queryKey: ['prompt', id],
    queryFn: () => getPrompt(id!),
    enabled: !!id,
  })
}

// Hook for updating prompt
export function useUpdatePrompt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePromptData }) =>
      updatePrompt(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['prompt', id] })
      queryClient.invalidateQueries({ queryKey: ['prompts'] })
    },
  })
}

// Hook for activating prompt (deactivates others with same use_case)
export function useActivatePrompt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => activatePrompt(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] })
    },
  })
}
